import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { branchAPI } from '../../services/api';
import ErrorModal from '../Common/ErrorModal';
import { AdminPage, StateBlock } from '../Common/admin';

const BranchView = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const loadBranch = useCallback(async () => {
    try {
      setLoading(true);
      const response = await branchAPI.getById(id);
      setRecord(response.data);
    } catch (err) {
      setErrorMessage(t('branch.view.messages.loadError'));
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    loadBranch();
  }, [loadBranch]);

  const headerActions = (
    <div className="ad-toolbar-group">
      <button type="button" className="ad-btn" onClick={() => navigate('/branch')}>
        <i className="fas fa-arrow-right"></i>
        {t('branch.view.backToList')}
      </button>
      <button
        type="button"
        className="ad-btn ad-btn-primary"
        onClick={() => navigate(`/branch/edit/${id}`)}
      >
        <i className="fas fa-pen"></i>
        {t('branch.view.edit')}
      </button>
    </div>
  );

  if (loading) {
    return (
      <AdminPage title={t('branch.view.title')}>
        <StateBlock icon="fa-hourglass-half" title={t('common.loading')} />
      </AdminPage>
    );
  }

  if (!record) {
    return (
      <AdminPage title={t('branch.view.title')}>
        <StateBlock icon="fa-circle-exclamation" title={t('branch.list.messages.noData')} />
        <ErrorModal
          show={showErrorModal}
          message={errorMessage}
          onClose={() => setShowErrorModal(false)}
        />
      </AdminPage>
    );
  }

  const items = [
    ['branchCode', record.branchCode],
    ['nameAr', record.branchNameAr],
    ['nameEng', record.branchNameEng],
    ['organization', record.organization?.organizationNameEng],
    ['country', record.country?.nameEng],
    ['area', record.area?.areaNameEng],
    ['city', record.city?.cityNameEng],
    ['district', record.district?.districtNameEng],
    ['streetName', record.streetName],
    ['buildingNumber', record.buildingNumber],
    ['additionalBuildingNumber', record.additionalBuildingNumber],
    ['zipCode', record.zipCode],
    ['crNumber', record.crNumber],
    ['vatNumber', record.vatNumber],
    ['vatRatio', record.vatRatio ? `${record.vatRatio}%` : null],
    ['telephoneNumber', record.telephoneNumber],
    ['email', record.email],
  ];

  return (
    <AdminPage
      title={record.branchNameAr || t('branch.view.title')}
      subtitle={record.branchNameEng}
      headerActions={headerActions}
    >
      <div className="ad-detail">
        <div className="ad-detail-grid">
          {items.map(([key, value]) => (
            <div key={key} className="ad-detail-item">
              <span className="ad-detail-label">{t(`branch.view.fields.${key}`)}</span>
              <span className="ad-detail-value">
                {value === null || value === undefined || value === '' ? '-' : value}
              </span>
            </div>
          ))}

          <div className="ad-detail-item">
            <span className="ad-detail-label">{t('branch.view.fields.isActive')}</span>
            <span>
              <span
                className={`ad-badge ${record.isActive ? 'ad-badge-success' : 'ad-badge-danger'}`}
              >
                {record.isActive ? t('common.active') : t('common.inactive')}
              </span>
            </span>
          </div>
        </div>
      </div>

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />
    </AdminPage>
  );
};

export default BranchView;
